import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import "@/App.css";

// Layouts
import { PublicLayout } from './components/layout/PublicLayout';
import { AdminLayout } from './components/layout/AdminLayout';

import { maintenanceAPI } from './lib/api';

// Landing Page
import { LandingPage } from './components/public/LandingPage';

// Public Pages
import { HomePage } from './components/public/HomePage';
import { NewsListPage } from './components/public/NewsListPage';
import { NewsDetailPage } from './components/public/NewsDetailPage';
import { GalleryPage } from './components/public/GalleryPage';
import { ClassesPage, ClassDetailPage } from './components/public/ClassesPage';
import { MenuPage } from './components/public/MenuPage';
import { DocumentsPage } from './components/public/DocumentsPage';
import { EventsPage } from './components/public/EventsPage';
import { ContactPage } from './components/public/ContactPage';
import { AboutPage, ParentsPage } from './components/public/ContentPages';
import { EnrollmentPage } from './components/public/EnrollmentPage';
import { StaffPage } from './components/public/StaffPage';

// Admin Auth Pages
import { LoginPage, RegisterPage, ForgotPasswordPage } from './components/admin/AuthPages';

// Admin Pages
import { DashboardPage } from './components/admin/DashboardPage';
import { NewsListAdmin, NewsFormAdmin } from './components/admin/NewsAdmin';
import { GalleryListAdmin, GalleryFormAdmin } from './components/admin/GalleryAdmin';
import { ClassesListAdmin, ClassFormAdmin } from './components/admin/ClassesAdmin';
import { MenuAdmin } from './components/admin/MenuAdmin';
import { DocumentsListAdmin, DocumentFormAdmin } from './components/admin/DocumentsAdmin';
import { EventsListAdmin, EventFormAdmin } from './components/admin/EventsAdmin';
import { MessagesAdmin } from './components/admin/MessagesAdmin';
import { UsersAdmin } from './components/admin/UsersAdmin';
import { PagesAdmin } from './components/admin/PagesAdmin';
import { StaffAdmin } from './components/admin/StaffAdmin';
import { MaintenanceAdmin } from './components/admin/MaintenanceAdmin';
import { BackupAdmin } from './components/admin/BackupAdmin';
import { LandingAdmin } from './components/admin/LandingAdmin';
import { ThemeAdmin } from './components/admin/ThemeAdmin';
import { AnalyticsAdmin } from './components/admin/AnalyticsAdmin';
import { canAccessNav } from './lib/utils';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F9FC]">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Admin Layout Wrapper
const AdminPage = ({ children }) => (
  <ProtectedRoute>
    <AdminLayout>{children}</AdminLayout>
  </ProtectedRoute>
);

// Role-protected admin route: pokud uživatel nemá oprávnění na daný modul, redirect na /admin
const RoleAdminPage = ({ navKey, children }) => {
  const { user } = useAuth();
  if (user && !canAccessNav(user.role, navKey)) {
    return <Navigate to="/admin" replace />;
  }
  return (
    <ProtectedRoute>
      <AdminLayout>{children}</AdminLayout>
    </ProtectedRoute>
  );
};

// Site-specific public routes with maintenance check
const SiteRoutes = ({ siteKey }) => {
  const [maintenance, setMaintenance] = React.useState(null);

  React.useEffect(() => {
    maintenanceAPI.get().then(res => setMaintenance(res.data)).catch(() => setMaintenance({ is_active: false }));
  }, []);

  if (maintenance === null) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F7F9FC]"><div className="spinner" /></div>;
  }

  if (maintenance.is_active) {
    return <Navigate to="/" replace />;
  }

  return (
    <PublicLayout siteKey={siteKey}>
      <Routes>
      <Route index element={<HomePage siteKey={siteKey} />} />
      <Route path="o-nas" element={<AboutPage siteKey={siteKey} />} />
      <Route path="aktuality" element={<NewsListPage siteKey={siteKey} />} />
      <Route path="aktuality/:id" element={<NewsDetailPage siteKey={siteKey} />} />
      <Route path="galerie" element={<GalleryPage siteKey={siteKey} />} />
      <Route path="tridy" element={<ClassesPage siteKey={siteKey} />} />
      <Route path="tridy/:id" element={<ClassDetailPage siteKey={siteKey} />} />
      <Route path="jidelnicek" element={<MenuPage siteKey={siteKey} />} />
      <Route path="dokumenty" element={<DocumentsPage siteKey={siteKey} />} />
      <Route path="kalendar" element={<EventsPage siteKey={siteKey} />} />
      <Route path="kontakt" element={<ContactPage siteKey={siteKey} />} />
      <Route path="pro-rodice" element={<ParentsPage siteKey={siteKey} />} />
      <Route path="zapis" element={<EnrollmentPage siteKey={siteKey} />} />
      <Route path="nas-tym" element={<StaffPage siteKey={siteKey} />} />
      <Route path="*" element={<Navigate to={`/${siteKey}`} replace />} />
    </Routes>
  </PublicLayout>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Landing Page / Rozcestnik */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Site-specific public routes */}
      <Route path="/ms/*" element={<SiteRoutes siteKey="ms" />} />
      <Route path="/ds/*" element={<SiteRoutes siteKey="ds" />} />

      {/* Admin Auth Routes */}
      <Route path="/admin/login" element={<LoginPage />} />
      <Route path="/admin/registrace" element={<RegisterPage />} />
      <Route path="/admin/zapomenute-heslo" element={<ForgotPasswordPage />} />

      {/* Admin Protected Routes */}
      <Route path="/admin" element={<AdminPage><DashboardPage /></AdminPage>} />
      <Route path="/admin/aktuality" element={<RoleAdminPage navKey="news"><NewsListAdmin /></RoleAdminPage>} />
      <Route path="/admin/aktuality/nova" element={<RoleAdminPage navKey="news"><NewsFormAdmin /></RoleAdminPage>} />
      <Route path="/admin/aktuality/:id" element={<RoleAdminPage navKey="news"><NewsFormAdmin /></RoleAdminPage>} />
      <Route path="/admin/galerie" element={<RoleAdminPage navKey="gallery"><GalleryListAdmin /></RoleAdminPage>} />
      <Route path="/admin/galerie/nova" element={<RoleAdminPage navKey="gallery"><GalleryFormAdmin /></RoleAdminPage>} />
      <Route path="/admin/galerie/:id" element={<RoleAdminPage navKey="gallery"><GalleryFormAdmin /></RoleAdminPage>} />
      <Route path="/admin/tridy" element={<RoleAdminPage navKey="classes"><ClassesListAdmin /></RoleAdminPage>} />
      <Route path="/admin/tridy/nova" element={<RoleAdminPage navKey="classes"><ClassFormAdmin /></RoleAdminPage>} />
      <Route path="/admin/tridy/:id" element={<RoleAdminPage navKey="classes"><ClassFormAdmin /></RoleAdminPage>} />
      <Route path="/admin/jidelnicek" element={<RoleAdminPage navKey="menu"><MenuAdmin /></RoleAdminPage>} />
      <Route path="/admin/dokumenty" element={<RoleAdminPage navKey="documents"><DocumentsListAdmin /></RoleAdminPage>} />
      <Route path="/admin/dokumenty/novy" element={<RoleAdminPage navKey="documents"><DocumentFormAdmin /></RoleAdminPage>} />
      <Route path="/admin/dokumenty/:id" element={<RoleAdminPage navKey="documents"><DocumentFormAdmin /></RoleAdminPage>} />
      <Route path="/admin/udalosti" element={<RoleAdminPage navKey="events"><EventsListAdmin /></RoleAdminPage>} />
      <Route path="/admin/udalosti/nova" element={<RoleAdminPage navKey="events"><EventFormAdmin /></RoleAdminPage>} />
      <Route path="/admin/udalosti/:id" element={<RoleAdminPage navKey="events"><EventFormAdmin /></RoleAdminPage>} />
      <Route path="/admin/zpravy" element={<RoleAdminPage navKey="messages"><MessagesAdmin /></RoleAdminPage>} />
      <Route path="/admin/uzivatele" element={<RoleAdminPage navKey="users"><UsersAdmin /></RoleAdminPage>} />
      <Route path="/admin/stranky" element={<RoleAdminPage navKey="pages"><PagesAdmin /></RoleAdminPage>} />
      <Route path="/admin/zamestnanci" element={<RoleAdminPage navKey="staff"><StaffAdmin /></RoleAdminPage>} />
      <Route path="/admin/udrzba" element={<RoleAdminPage navKey="maintenance"><MaintenanceAdmin /></RoleAdminPage>} />
      <Route path="/admin/zalohy" element={<RoleAdminPage navKey="backup"><BackupAdmin /></RoleAdminPage>} />
      <Route path="/admin/rozcestnik" element={<RoleAdminPage navKey="landing"><LandingAdmin /></RoleAdminPage>} />
      <Route path="/admin/motiv" element={<RoleAdminPage navKey="theme"><ThemeAdmin /></RoleAdminPage>} />
      <Route path="/admin/statistika" element={<RoleAdminPage navKey="analytics"><AnalyticsAdmin /></RoleAdminPage>} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster 
            position="top-right" 
            richColors 
            toastOptions={{
              style: {
                fontFamily: 'Inter, sans-serif',
              }
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
